import {
    AnyValue,
    Property,
    isIntegerValue,
    isStringValue,
    isBooleanValue,
    isTextValue,
    isFloatValue,
    isDateValue,
    isDateTimeValue,
    isLinkValue,
    isRatingValue,
    isRelationOneValue,
    isEnumValue,
} from '@varos/research-sdk';
import { assert, assertNever } from '@varos/util-typescript';
import { ReportingContainerBaseConfig, ReportingControllerBaseConfig } from '../../base';
import { ValueController } from './valueInterface';
import { ValueViewProps } from './valueProps';
import { useMemo } from 'react';
export function createValueController(
    config: ReportingControllerBaseConfig
): ValueController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;
    function getValueProps(
        property: Exclude<Property, { type: 'relation' }>,
        value: Exclude<AnyValue, null>
    ): ValueViewProps['value'] {
        if (property.type === 'link') {
            assert(isLinkValue(value), 'Value must be a link');
            return {
                kind: 'link',
                label: value.label ?? property.display_name,
                href: value.href,
            };
        }
        if (property.type === 'integer') {
            assert(isIntegerValue(value), 'Value must be a number');
            return {
                kind: 'text',
                label: value.toString(),
            };
        }
        if (property.type === 'boolean') {
            assert(isBooleanValue(value), 'Value must be a boolean');
            return {
                kind: 'boolean',
                raw: value,
                label: value ? 'True' : 'False',
            };
        }
        if (property.type === 'string' || property.type === 'text') {
            assert(isStringValue(value) || isTextValue(value), 'Value must be a string');
            return {
                kind: 'text',
                label: value,
            };
        }
        if (property.type === 'float') {
            assert(isFloatValue(value), 'Value must be a number');
            return {
                kind: 'text',
                label: value.toString(),
            };
        }
        if (property.type === 'date') {
            assert(isDateValue(value), 'Value must be a string');
            return {
                kind: 'text',
                label: formatter.date(value),
            };
        }
        if (property.type === 'datetime') {
            assert(isDateTimeValue(value), 'Value must be a string');
            return {
                kind: 'text',
                label: formatter.date(value),
            };
        }
        if (property.type === 'enum') {
            assert(isEnumValue(value), 'Value must be an enum');
            // TODO optimize this by grouping into lookup table before calling this function
            const option = property.config.options.find(
                (option) => option.value === value
            );
            return {
                kind: 'enum',
                raw: value,
                label: option?.label ?? value,
                icon: option?.icon
                    ? {
                          kind: 'emoji',
                          text: option.icon,
                      }
                    : null,
                options: property.config.options.map((option) => ({
                    icon: option.icon
                        ? {
                              kind: 'emoji',
                              text: option.icon,
                          }
                        : null,
                    value: option.value,
                    label: option.label,
                    description: option.description ?? null,
                })),
            };
        }
        if (property.type === 'rating') {
            assert(isRatingValue(value), 'Value must be a rating');
            return {
                kind: 'rating',
                value: value,
                label: value.toString(),
                min: property.config.min,
                max: property.config.max,
            };
        }

        assertNever(property);
    }
    return {
        useProps(context, item, props): ValueViewProps {
            const value = useMemo(() => {
                if (item.value === null) {
                    return null;
                }
                assert(
                    item.property.type !== 'relation',
                    'Relation properties are handled separately'
                );
                return getValueProps(item.property, item.value);
            }, [item.property, item.value]);
            return {
                property: {
                    id: item.property.key,
                    label: item.property.display_name,
                },
                tooltip: {
                    label: item.property.display_name,
                },
                value,
            };
        },
    };
}
